import {AlertIcon, CopilotIcon} from '@primer/octicons-react'
import {Spinner} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {useEffect, useRef, type RefObject} from 'react'

type FailureAnalysisStatus = 'loading' | 'empty' | 'error' | 'analysis-failed' | 'analysis-unavailable'

const ERROR_MESSAGE = 'Failed to load failure analysis. Try reloading the page.'
const EMPTY_MESSAGE = 'No failure analysis is available for these checks.'
export const ANALYSIS_FAILED_MESSAGE = "Copilot couldn't complete CI failure analysis."
export const ANALYSIS_UNAVAILABLE_MESSAGE = 'Analysis results unavailable.'
export const PARTIAL_ANALYSIS_FAILED_MESSAGE = "Copilot couldn't complete CI failure analysis. Showing partial results."
export const PARTIAL_ANALYSIS_UNAVAILABLE_MESSAGE = 'Analysis timed out. Showing partial results.'

export function useAnalysisWarningMessage({
  isAnalysisFailed,
  isAnalysisUnavailable,
  hasFindings,
  announcementElementRef,
}: {
  isAnalysisFailed: boolean
  isAnalysisUnavailable: boolean
  hasFindings: boolean
  announcementElementRef?: RefObject<HTMLElement | null>
}) {
  const message = isAnalysisFailed
    ? PARTIAL_ANALYSIS_FAILED_MESSAGE
    : isAnalysisUnavailable
      ? PARTIAL_ANALYSIS_UNAVAILABLE_MESSAGE
      : undefined

  useEffect(() => {
    if (message && hasFindings) {
      announce(message, {element: announcementElementRef?.current ?? undefined})
    }
  }, [announcementElementRef, hasFindings, message])

  return message
}

const statusConfig: Record<FailureAnalysisStatus, {icon: React.ReactNode; message: React.ReactNode}> = {
  loading: {
    icon: <Spinner size="medium" />,
    message: (
      <>
        <strong>Copilot</strong> is analyzing CI failures
      </>
    ),
  },
  empty: {
    icon: <CopilotIcon size={24} className="fgColor-muted" />,
    message: EMPTY_MESSAGE,
  },
  error: {
    icon: <AlertIcon size={24} className="fgColor-attention" />,
    message: ERROR_MESSAGE,
  },
  'analysis-failed': {
    icon: <AlertIcon size={24} className="fgColor-attention" />,
    message: ANALYSIS_FAILED_MESSAGE,
  },
  'analysis-unavailable': {
    icon: <AlertIcon size={24} className="fgColor-attention" />,
    message: ANALYSIS_UNAVAILABLE_MESSAGE,
  },
}

export function FailureAnalysisBlankslate({status}: {status: FailureAnalysisStatus}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const {icon, message} = statusConfig[status]

  useEffect(() => {
    if (status === 'error') {
      announce(ERROR_MESSAGE, {element: containerRef.current ?? undefined})
    } else if (status === 'empty') {
      announce(EMPTY_MESSAGE, {element: containerRef.current ?? undefined})
    } else if (status === 'analysis-failed') {
      announce(ANALYSIS_FAILED_MESSAGE, {element: containerRef.current ?? undefined})
    } else if (status === 'analysis-unavailable') {
      announce(ANALYSIS_UNAVAILABLE_MESSAGE, {element: containerRef.current ?? undefined})
    }
  }, [status])

  return (
    <div ref={containerRef} className="p-4 d-flex flex-column flex-items-center gap-3">
      {icon}
      <span className="fgColor-muted text-small">{message}</span>
    </div>
  )
}
